import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AuthState } from "@/lib/types/auth";
import { useAuthState } from "../auth/hooks/useAuthState";
import { useAuthHandlers } from "../auth/hooks/useAuthHandlers";
import { useToast } from "@/components/ui/use-toast";

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string, 
    schoolDistrict: string, 
    school: string, 
    position: string,
    customSchoolDistrict?: string,
    customSchool?: string,
    customPosition?: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  isInitialized: boolean;
  updateAuthState: (user: User | null, skipProfileFetch?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { state, updateAuthState } = useAuthState();
  const { signIn: handleSignIn, signUp: handleSignUp, signOut: handleSignOut } = useAuthHandlers(supabase);
  const [isInitialized, setIsInitialized] = useState(false);
  const { toast } = useToast();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [hasShownWelcomeToast, setHasShownWelcomeToast] = useState(false);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        console.log("Initializing auth context...");
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Error getting initial session:", sessionError);
          // Toast disabled
          if (mounted) {
            updateAuthState(null);
            setIsInitialized(true);
          }
          return;
        }

        console.log("Initial session check:", session ? "Found session" : "No session");
        
        if (mounted) {
          updateAuthState(session?.user ?? null);
          setIsInitialized(true);
        }

        const { data: { subscription }} = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log("Auth state changed:", event, session?.user?.id);
          if (mounted) {
            if (['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
              updateAuthState(session?.user ?? null);
              // Toast disabled
              setHasShownWelcomeToast(true);
            } else if (event === 'SIGNED_OUT' && !isSigningOut) {
              updateAuthState(null);
              setHasShownWelcomeToast(false);
              // Toast disabled
            }
          }
        });

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error initializing auth:", error);
        // Toast disabled
        if (mounted) {
          updateAuthState(null);
          setIsInitialized(true);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [updateAuthState, toast, isSigningOut, hasShownWelcomeToast]);

  const signIn = async (email: string, password: string) => {
    try {
      console.log("AuthContext: Signing in user", email);
      const data = await handleSignIn(email, password);
      updateAuthState(data.user);
    } catch (error) {
      console.error("Sign in error:", error);
      
      // Toast disabled
      throw error; // Re-throw for component-level handling
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string, 
    schoolDistrict: string, 
    school: string, 
    position: string,
    customSchoolDistrict: string = "",
    customSchool: string = "",
    customPosition: string = ""
  ) => {
    try {
      console.log("AuthContext: Starting signup process");
      const data = await handleSignUp(
        email, 
        password, 
        firstName, 
        lastName, 
        schoolDistrict, 
        school, 
        position,
        customSchoolDistrict,
        customSchool,
        customPosition
      );
      
      if (!data.user) {
        throw new Error("No user data returned from signup");
      }
      
      console.log("AuthContext: Signup successful, updating auth state");
      await updateAuthState(data.user, false); // Force profile fetch
      
      toast({
        title: "Welcome!",
        description: "Your account has been created successfully.",
      });
    } catch (error) {
      console.error("Sign up error:", error);
      
      // Clean up auth state regardless of error type
      try {
        await supabase.auth.signOut();
        await updateAuthState(null);
      } catch (cleanupError) {
        console.error("Error during cleanup:", cleanupError);
      }
      
      // Handle specific error cases
      if (error.message?.toLowerCase().includes("rate limit")) {
        toast({
          variant: "destructive",
          title: "Too Many Attempts",
          description: "Please wait a few minutes before trying to sign up again.",
        });
      } else if (error.message?.includes("profile")) {
        toast({
          variant: "destructive",
          title: "Profile Creation Failed",
          description: "There was an error creating your profile. Please try signing up again.",
        });
      } else if (error.message?.includes("timed out")) {
        toast({
          variant: "destructive",
          title: "Request Timeout",
          description: "The signup request timed out. Please try again when the service is less busy.",
        });
      } else if (error.message?.includes("already registered")) {
        toast({
          variant: "destructive",
          title: "Email Already Registered",
          description: "This email is already registered. Please try signing in instead.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Sign Up Failed",
          description: error.message || "An unexpected error occurred during sign up. Please try again.",
        });
      }
      
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setIsSigningOut(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log("No active session found during signOut");
        updateAuthState(null);
        return;
      }
      await handleSignOut();
      updateAuthState(null);
      setHasShownWelcomeToast(false);
      
      // Toast disabled
    } catch (error) {
      console.error("Error during sign out:", error);
      // Toast disabled
      updateAuthState(null); // Still clear the auth state
    } finally {
      setIsSigningOut(false);
    }
  };

  const contextValue = useMemo(() => ({
    ...state,
    signIn,
    signUp,
    signOut,
    isInitialized,
    updateAuthState
  }), [state, signIn, signUp, signOut, isInitialized, updateAuthState]);

  if (!isInitialized) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
